import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

export interface TabOption {
	id: string;
	label: string;
	icon?: IconDefinition;
	disabled?: boolean;
	/**
	 * A count shown after the label as a small pill, the way the list tabs
	 * show theirs ("Open 23"). A bare number appended to the label read as
	 * part of the name ("Recipients 1").
	 */
	count?: number;
}

/**
 * A tab's count as a small pill beside its name, the style the list tabs use
 * ("Open 23"). Shared so every tab strip shows counts the same way: a bare
 * number after a name ("Recipients 1") read as part of the name.
 */
export const TabCountPill: React.FC<{ count: number }> = ({ count }) => {
	const colors = useThemeColors();

	return (
		<span
			className={cn(
				'flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full border px-1.5 py-0.5 text-xs font-medium tabular-nums',
				colors.classes.bgSecondary,
				colors.classes.textSecondary,
				colors.classes.borderDefault,
			)}
		>
			{count}
		</span>
	);
};

interface TabsProps {
	tabs: TabOption[];
	activeTab: string;
	onTabChange: (tabId: string) => void;
	variant?: 'default' | 'pills' | 'underline';
	alignment?: 'left' | 'center' | 'right';
	showIcons?: boolean;
	className?: string;
	/**
	 * Run tabs onto a second line instead of scrolling them sideways. For a strip
	 * in a narrow pane, where a scrolled-off tab would be easy to miss.
	 */
	wrap?: boolean;
}

export const Tabs: React.FC<TabsProps> = ({
	tabs,
	activeTab,
	onTabChange,
	variant = 'default',
	alignment = 'left',
	showIcons = false,
	className,
	wrap = false,
}) => {
	const colors = useThemeColors();
	const listRef = useRef<HTMLDivElement | null>(null);

	// At phone width a six-tab strip is wider than the screen. The strip
	// scrolls sideways instead of overflowing the page, and the active tab is
	// kept in view so a deep link to the last tab does not open on a strip
	// that looks like it ends at the third (PD-15939).
	useEffect(() => {
		const list = listRef.current;
		if (list == null) {
			return;
		}
		const active = list.querySelector<HTMLElement>('[aria-selected="true"]');
		if (active != null && list.scrollWidth > list.clientWidth) {
			active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
		}
	}, [activeTab]);

	const alignmentClasses = {
		left: 'justify-start',
		center: 'justify-center',
		right: 'justify-end',
	};

	const getTabClasses = (tab: TabOption, isActive: boolean) => {
		// PD-16082 — the ring only for keyboard focus. `focus:` painted a box
		// after every mouse click, clipped to three sides by the scrolling
		// tablist, until the next click elsewhere; the active underline is the
		// selection cue, and `focus-visible` keeps the ring for Tab navigation.
		const baseClasses = cn(
			'flex-none whitespace-nowrap px-4 py-2 font-medium text-sm transition-colors duration-150',
			'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset',
			tab.disabled && 'opacity-50 cursor-not-allowed',
		);

		switch (variant) {
			case 'pills':
				return cn(
					baseClasses,
					'rounded-md',
					// The standard selected-control treatment. bgPrimary is the
					// surface color, so the active pill read as white text on a
					// white page in the light theme.
					isActive
						? colors.classes.controlSelected
						: cn(
								colors.classes.textPrimary,
								'hover:' + colors.classes.bgSecondary,
							),
				);
			case 'underline':
				return cn(
					baseClasses,
					'border-b-2',
					isActive
						? cn('border-accent', colors.classes.textPrimary)
						: cn(
								'border-transparent',
								colors.classes.textPrimary,
								'hover:opacity-80 hover:border-strong',
							),
				);
			default:
				return cn(
					baseClasses,
					'border-b-2',
					isActive
						? cn('border-accent', colors.classes.textPrimary)
						: cn(
								'border-transparent',
								colors.classes.textSecondary,
								'hover:' + colors.classes.textPrimary,
							),
				);
		}
	};

	return (
		<div className={cn('w-full', className)}>
			<div
				ref={listRef}
				role="tablist"
				className={cn(
					'flex',
					wrap ? 'flex-wrap' : 'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
					alignmentClasses[alignment],
					variant === 'underline' &&
						cn('border-b', colors.classes.borderDefault),
				)}
			>
				{tabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						// role="tab" + aria-selected aligns with the WAI-ARIA tab
						// pattern. Without these, screen readers announce the tab
						// strip as a generic group of buttons, and tests using
						// `getByRole('tab', ...)` (the conventional accessible
						// selector) can't find tabs at all.
						role="tab"
						aria-selected={activeTab === tab.id}
						aria-disabled={tab.disabled === true ? true : undefined}
						onClick={() => !tab.disabled && onTabChange(tab.id)}
						className={getTabClasses(tab, activeTab === tab.id)}
						disabled={tab.disabled}
					>
						{/* PD-14355: plain spans so the label inherits the button's
						    text-sm. The previous nested <Text> defaulted to text-base,
						    overriding the intended size and making every tab label
						    across the app look oversized/inconsistent. */}
						<span className="flex items-center space-x-2">
							{showIcons && tab.icon && (
								<FontAwesomeIcon icon={tab.icon} className="w-4 h-4" />
							)}
							<span>{tab.label}</span>
							{tab.count != null && <TabCountPill count={tab.count} />}
						</span>
					</button>
				))}
			</div>
		</div>
	);
};
